import { KeyedMutator } from 'swr';

// Utils
import useUserMarketing from '@/utils/hooks/swr/useUserMarketing';
import useTranslation from '@/utils/hooks/translation/useTranslation';
import useAcl from '@/utils/hooks/user/useAcl';

import { mixPanelSubscriptionCancellationFlowCompleted } from '@/utils/mixpanel/subscriptionCancellationFlowEvents';
import useToggle from '@/utils/hooks/utils/useToggle';

// Types
import type { ActivePlanType, SubscriptionType } from '@/types/pricingTypes/subscriptionType';
import type { RetainCancellationFlowShownResult } from '@paddle/paddle-js/types/checkout/retain';

// Lib
import { aclTemporaryFeatures } from '@/lib/acl/aclFeatures';
import { subscriptionStates } from '@/lib/settings/subscriptionInvoices/subscriptionState';

// Components
import Tooltip from '@/components/base/Tooltip';
import SubscriptionCancellationModal from '@/components/pages/settings/subscription-invoices/PageContent/Components/SubscriptionCancellation/SubscriptionCancellationModal';

type SubscriptionCancellationProps = {
    subscription: ActivePlanType;
    mutateSubscription: KeyedMutator<SubscriptionType>;
};

const SubscriptionCancellation = ({
    subscription,
    mutateSubscription
}: SubscriptionCancellationProps) => {
    const { userMarket } = useUserMarketing();
    const { hasAcl } = useAcl();
    const { t } = useTranslation('settings');
    const { isOpen, toggle: toggleCancellationModal } = useToggle();
    const hasCSMManager = !!userMarket?.data?.csm_manager?.email;
    const isSandBox = process.env.NEXT_PUBLIC_PADDLE_ENVIRONMENT === 'sandbox';

    const hasCancellationRight = hasAcl(aclTemporaryFeatures.account.cancel_subscription.delete);

    const isManualSubscription = subscription?.is_manual;

    function hasCsmPlan(): boolean {
        return (
            subscription?.product_key === 'excel' ||
            subscription?.product_key === 'agency' ||
            (subscription?.product_key === 'teams' &&
                subscription?.options?.profiles?.count_total > 10) ||
            hasCSMManager
        );
    }

    function onInitCancellationFlow() {
        if (hasCsmPlan()) {
            return toggleCancellationModal();
        }

        const { Paddle } = window;
        if (!Paddle) return;

        if (!isSandBox) {
            return Paddle.Retain?.initCancellationFlow({
                subscriptionId: subscription?.subscription_id
            })?.then((res: RetainCancellationFlowShownResult) => {
                mixPanelSubscriptionCancellationFlowCompleted(res);
                mutateSubscription();
            });
        }
    }

    if (isManualSubscription) {
        return null;
    }

    return (
        <>
            <Tooltip
                disabled={hasCancellationRight}
                interactive
                content={t('subscription_invoices_page.cancel_subscription_not_available_tooltip')}>
                <span>
                    <button
                        onClick={onInitCancellationFlow}
                        disabled={!hasCancellationRight}
                        className="text-xs text-icoBlue underline focus:ring-0 focus:ring-offset-0 disabled:text-icoGray-400 disabled:dark:text-icoDarkMode-wolf"
                        data-testid="subscription-cancellation-modal-button">
                        {t(subscriptionStates.default.title)}
                    </button>
                </span>
            </Tooltip>
            <SubscriptionCancellationModal
                isOpen={isOpen}
                toggle={toggleCancellationModal}
                csmManagerEmail={userMarket?.data?.csm_manager?.email}
            />
        </>
    );
};

export default SubscriptionCancellation;
